/* eslint-disable consistent-return */
import { Bert } from 'meteor/themeteorchef:bert';
import { isNil, forEach } from 'lodash';

import * as projectOps from './project-operations';

import methodPromise from '../../modules/method-promise-wrapper';
import handleErrorInClient from '../../modules/handle-error-in-client';

/**
 * Checks if the DTM for the project is available in server.
 *
 * @param {string} projectID - The project id.
 * @returns {boolean} True if the file is available, false otherwise.
 * @throws {Error} If project is null or undefined.
 */
export const isProjectDTMAvailableValid = async projectID => {
  try {
    if (isNil(projectID)) {
      throw new Error('project not defined');
    }
    const isDTMAvailableValid = await methodPromise('projects.isDTMAvailableValid', projectID);
    return isDTMAvailableValid;
  } catch (error) {
    handleErrorInClient(error);
  }
};

/**
 * Soft-deletes the project in the database and moves the page to the project page.
 *
 * @param {string} projectID - The project id.
 * @param {object} history - The router history object.
 * @throws {Error} If project is null or undefined.
 */
export const projectSoftDelete = async (projectID, history) => {
  try {
    if (isNil(projectID)) {
      throw new Error('project not defined');
    }
    await methodPromise('projects.softDelete', projectID);
    Bert.alert('Project moved to Trash!', 'warning');
    history.push('/projects');
  } catch (error) {
    handleErrorInClient(error);
  }
};

/**
 * Gets the project DTM file from the bounding box, deleting the existing one.
 *
 * @param {object} projectObject - The project object.
 * @throws {Error} If project is null or undefined.
 */
export const createDTMFileForProject = async projectObject => {
  try {
    if (isNil(projectObject)) {
      throw new Error('project not defined');
    }
    await methodPromise('projects.createDTMFile', projectOps.getId(projectObject));
    Bert.alert('File Downloaded', 'success');
  } catch (error) {
    handleErrorInClient(error);
  }
};

/**
 * Gets the project DTM file from the bounding box, deleting the existing one.
 *
 * @param {object} projectObject - The project object.
 * @throws {Error} If project is null or undefined.
 */
export const deleteDTMFile = async projectObject => {
  try {
    if (isNil(projectObject)) {
      throw new Error('project not defined');
    }
    await methodPromise('projects.deleteDTMFile', projectOps.getId(projectObject));
    Bert.alert('Deleted File', 'danger');
  } catch (error) {
    handleErrorInClient(error);
  }
};

/**
 * Shares or unshares a project.
 *
 * @param {object} projectObject - The project object.
 * @throws {Error} If project is null or undefined.
 */
export const projectToggleShared = async projectObject => {
  try {
    if (isNil(projectObject)) {
      throw new Error('project not defined');
    }
    await methodPromise(
      'projects.toggleShared',
      projectOps.getId(projectObject),
      !projectOps.isShared(projectObject)
    );
    if (!projectOps.isShared(projectObject)) {
      Bert.alert('Link Created', 'success');
    } else {
      Bert.alert('No longer Shared', 'info');
    }
  } catch (error) {
    handleErrorInClient(error);
  }
};

/**
 * Copies a project and its items (currently only videos and annotations) in the database.
 *
 * @param {object} projectObject - The project object.
 * @throws {Error} If project is null or undefined.
 */
export const projectCopy = async projectObject => {
  try {
    if (isNil(projectObject)) {
      throw new Error('project not defined');
    }
    const newProjectId = await methodPromise('projects.copy', projectOps.getId(projectObject));
    const newVideosIds = await methodPromise(
      'videos.copyFromProject',
      projectOps.getId(projectObject),
      newProjectId
    );
    forEach(newVideosIds, ({ originVideoId, destVideoId }) => {
      methodPromise('annotations.copy', originVideoId, destVideoId);
    });
    Bert.alert('Project copied', 'success');
  } catch (error) {
    handleErrorInClient(error);
  }
};
